import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  Box,
  Button,
  ButtonToggle,
  Flex,
  Typography,
  useDesktopMediaQuery,
  useTabletMediaQuery,
} from '@iheartjane/reefer';
import type { ShopperReservationMode } from '@iheartjane/types';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useCheckout } from '@jane/shared-ecomm/providers';
import { useJaneUser } from '@jane/shared/data-access';

import { useCheckoutAccordion } from '../CheckoutAccordion/CheckoutAccordionProvider';
import { useAvailableShopperReservationModes } from '../util/hooks/useAvailableShopperReservationModes';
import { useFulfillmentSchedule } from '../util/hooks/useFulfillmentSchedule';
import { useReservationModeLabels } from '../util/hooks/useReservationModeLabels';
import { resolveFulfillmentSlot } from '../util/resolveFulfillmentSlot';
import { modesWithSlots, resolveModeChange } from '../util/resolveModeChange';
import { resolveSelectedMode } from '../util/resolveSelectedMode';

interface CheckoutHeaderProps {
  handleShowLogin: () => void;
}

export const CheckoutToggle = ({ handleShowLogin }: CheckoutHeaderProps) => {
  const { data: janeUserData } = useJaneUser();

  const isMobile = useTabletMediaQuery({ width: 'max' });
  const isDesktop = useDesktopMediaQuery({ size: 'sm', width: 'min' });
  const {
    appInfo: { disableAuthFeatures },
    cart,
    dispatches: { changeReservationMode, setBagPickupMode },
    store,
  } = useCheckout();
  const { setExpanded } = useCheckoutAccordion();
  const reservationMode = cart?.reservation_mode;

  const { deliveryLabel, curbsideLabel, pickupLabel } =
    useReservationModeLabels();

  const { control, getValues, setValue } = useFormContext();
  const pickupMode = useWatch({ control, name: 'pickupOptions.pickupMode' });

  const {
    curbsideSlots,
    deliverySlots,
    hasFetchedCurbside,
    hasFetchedDelivery,
    hasFetchedPickup,
    isAcceptingCurbside,
    isAcceptingDelivery,
    isAcceptingPickup,
    pickupSlots,
    slotsSettled,
  } = useFulfillmentSchedule();

  const signedIn = Boolean(janeUserData?.user?.id);
  const showLogin = !signedIn && isMobile && !disableAuthFeatures;

  const curbsideFlagOn = useFlag(FLAGS.c1CurbsideFulfillment) ?? false;
  const availableReservationModes = useAvailableShopperReservationModes(
    store,
    undefined,
    isAcceptingCurbside
  );

  const selectedMode = resolveSelectedMode(
    reservationMode,
    pickupMode,
    availableReservationModes
  );

  const isDeliveryOpen =
    isAcceptingDelivery && !(hasFetchedDelivery && !deliverySlots.length);

  // Built from slot query results, to be used only for picking the initial mode.
  const slotOpenModes = modesWithSlots(availableReservationModes, {
    curbside: curbsideSlots.length > 0,
    delivery: isDeliveryOpen,
    pickup: pickupSlots.length > 0,
  });

  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (!curbsideFlagOn) return;
    if (hasSeededRef.current) return;
    if (!slotsSettled) return;
    hasSeededRef.current = true;

    const modeChange = resolveModeChange(
      reservationMode,
      pickupMode,
      slotOpenModes,
      availableReservationModes
    );
    if (!modeChange) return;

    if (pickupMode === 'curbside' && modeChange !== 'curbside') {
      setBagPickupMode('pickup');
      setValue('pickupOptions.pickupMode', 'pickup');
    }
    if (modeChange === 'curbside' && pickupMode !== 'curbside') {
      setBagPickupMode('curbside');
      setValue('pickupOptions.pickupMode', 'curbside');
    }
    const wireMode = modeChange === 'delivery' ? 'delivery' : 'pickup';
    if (wireMode !== reservationMode) {
      changeReservationMode(wireMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    curbsideFlagOn,
    slotsSettled,
    slotOpenModes,
    availableReservationModes,
    reservationMode,
    pickupMode,
  ]);

  const getLabelFor = (mode: ShopperReservationMode) => {
    if (mode === 'delivery') return deliveryLabel;
    // curbside is only ever in `modes` when the flag is on (gated upstream in
    // useAvailableShopperReservationModes), so no extra flag check is needed here.
    if (mode === 'curbside') return curbsideLabel;
    // Flag off: the single Pickup button falls back to the curbside label for
    // mandatory-curbside stores. Flag on: curbside has its own button.
    if (curbsideFlagOn) return pickupLabel;
    return isAcceptingPickup ? pickupLabel : curbsideLabel;
  };

  const handleToggle = (mode: ShopperReservationMode) => {
    if (!curbsideFlagOn) {
      changeReservationMode(mode as 'delivery' | 'pickup');
      setExpanded('options');
      return;
    }

    if (mode === 'delivery') {
      changeReservationMode('delivery');
      // Normalize the pickup sub-mode so a prior 'curbside' choice doesn't
      // linger and re-seed the checkout form if the shopper switches back.
      setBagPickupMode('pickup');
    } else {
      const nextPickupMode = mode === 'curbside' ? 'curbside' : 'pickup';

      // Reassign fulfillmentTime to a matching slot in the new mode, else the
      // "time slot no longer available" alert flashes briefly.
      if (pickupMode !== nextPickupMode) {
        const targetSlots =
          nextPickupMode === 'pickup' ? pickupSlots : curbsideSlots;
        setValue(
          'pickupOptions.fulfillmentTime',
          resolveFulfillmentSlot(
            getValues('pickupOptions.fulfillmentTime'),
            targetSlots
          )
        );
      }

      // Wire contract: cart's reservation_mode is only ever 'delivery' or 'pickup'.
      changeReservationMode('pickup');
      setValue('pickupOptions.pickupMode', nextPickupMode);
      // Persist to Redux/Storage so the choice survives a refresh (the form's
      // pickupOptions.pickupMode is in-memory only).
      setBagPickupMode(nextPickupMode);

      // Pickup⇄Curbside doesn't change reservation_mode, so no natural scroll
      // fires. Desktop only — on mobile it pushes the tapped toggle off-screen.
      if (!isMobile) {
        document
          .getElementById('accordion-heading-options')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setExpanded('options');
  };

  return (
    <Box
      background="grays-white"
      mb={isDesktop ? 24 : 16}
      py={isDesktop ? 0 : 16}
      px={isDesktop ? 0 : 24}
      width="100%"
    >
      {showLogin && (
        <Button
          mb={16}
          full
          label="Sign in"
          variant="secondary"
          part="sign-in"
          onClick={handleShowLogin}
        />
      )}
      {availableReservationModes.length > 1 && (
        <ButtonToggle
          variant="tertiary"
          value={curbsideFlagOn ? selectedMode : reservationMode}
          onChange={(mode) => handleToggle(mode as ShopperReservationMode)}
        >
          {availableReservationModes.map((mode) => (
            <ButtonToggle.Button
              value={mode}
              key={mode}
              disabled={
                mode === 'delivery'
                  ? !isDeliveryOpen
                  : mode === 'pickup'
                    ? // Flag off: the single Pickup button also serves curbside
                      // (mandatory-curbside stores), so only disable it when
                      // neither is accepting — matching pre-PR behavior. Flag on:
                      // curbside has its own button, so disable strictly on pickup.
                      curbsideFlagOn
                      ? !isAcceptingPickup ||
                        (!pickupSlots.length && hasFetchedPickup)
                      : !isAcceptingPickup && !isAcceptingCurbside
                    : !isAcceptingCurbside ||
                      (!curbsideSlots.length && hasFetchedCurbside)
              }
            >
              <Typography
                as="span"
                variant="body-semibold"
                maxLines={2}
                overflowWrap="break-word"
              >
                {getLabelFor(mode)}
              </Typography>
            </ButtonToggle.Button>
          ))}
        </ButtonToggle>
      )}

      {availableReservationModes.length === 1 && (
        <Flex width="100%" justifyContent="center" mt={24}>
          <Typography variant="body-semibold">
            {getLabelFor(availableReservationModes[0])}
          </Typography>
        </Flex>
      )}
    </Box>
  );
};
